import type { VariantProps } from "tailwind-variants";

import { tv } from "../utils/tv";

/**
 * Link wrapper **Tailwind Variants** component
 *
 * @example
 * <a className={link({ color: "secondary", isBlock: true })} href="#" />
 */
const link = tv({
  base: [
    [
      "relative",
      "inline-flex items-center gap-2",
      "outline-none tap-highlight-transparent",
      "rounded-xl",
      "box-border",
      "font-semibold",
      "cursor-pointer",
      "transition-all",
    ],
  ],
  variants: {
    variant: {
      default: [""],
      underline: ["border-transparent"],
      box: [
        "py-1 px-2",
        "border border-neutral-200",
        "bg-neutral-25 rounded-xl",
        "hover:bg-neutral-50",
        "active:bg-neutral-100",
      ],
    },
    size: {
      sm: ["text-sm "],
      md: ["text-medium"],
      lg: ["text-large"],
    },
    color: {
      foreground: "text-neutral-500 hover:text-neutral-700 active:text-neutral-900",
      primary: "text-primary hover:text-primary-700 active:text-primary-900",
      secondary: "text-secondary hover:text-secondary-700 active:text-secondary-900",
      success: "text-success hover:text-success-700 active:text-success-900",
      warningO: "text-warningO hover:text-warningO-700 active:text-warningO-900",
      danger: "text-danger hover:text-danger-700 active:text-danger-900",
    },
    underline: {
      none: "no-underline",
      hover: "hover:underline",
      always: "underline",
      active: "active:underline",
      focus: "focus:underline",
    },
    isBlock: {
      true: [""],
      false: "",
    },
    isDisabled: {
      true: "opacity-disabled cursor-default pointer-events-none",
    },
    disableAnimation: {
      true: "after:transition-none transition-none",
    },
  },
  defaultVariants: {
    variant: "default",
    color: "primary",
    size: "sm",
    isBlock: false,
    underline: "none",
    isDisabled: false,
  },
  compoundVariants: [
    {
      underline: ["hover", "always", "active", "focus"],
      class: "underline-offset-4",
    },
    {
      variant: "box",
      size: "md",
      class: "py-2 px-3",
    },
    {
      variant: "box",
      size: "lg",
      class: "p-3",
    },
  ],
});

export const linkAnchorClasses = "flex shrink-0 text-current self-center scale-125";

export type LinkVariantProps = VariantProps<typeof link>;

export { link };
